import { watch } from 'vue';

import { invalidateNotifications } from '@/domains/notifications/queries';
import { i18n } from '@/lang';
import { beginSignOut, commitSignOut, endSignOut } from '@/platform/auth-runtime/sign-out-lock';
import { useAuthStore } from '@/platform/auth-runtime/store';
import { getSubBus } from '@/platform/host-sdk/host-bridge';
import { refreshAccountInfo } from '@/platform/identity/queries';
import { useUserStore } from '@/platform/identity/store';
import { Path } from '@/platform/routing/targets';
import { reportManualError } from '@/platform/telemetry/frontend-alert';
import { queryClient } from '@/shared/lib/query-client';
import { wsData } from '@/shared/lib/ws/ws';
import { isString } from '@/shared/utils/is';
import { HOST_EVENTS, emitMessage } from '@celerdata/host-bridge-contract';
import { useDebounceFn } from '@vueuse/core';

import type { FormattedUnreadList } from '@/domains/notifications/queries';

const FORCED_LOGOUT_CODE = 50001;
const UNREAD_KEY = ['notification', 'unread-list'] as const;

// Shared with queries.ts mutations — invalidates the v3 unread cache AND
// emits the host bridge invalidate signal so the legacy /notification view
// refetches its own list. Debounced 1s to coalesce rapid WS bursts.
const refresh = useDebounceFn(() => {
  invalidateNotifications(queryClient);
}, 1000);

function currentUnreadKey(): readonly unknown[] {
  return [...UNREAD_KEY, useUserStore().info?.accountID ?? null];
}

function removeBannerLocally(notificationId: string): boolean {
  const key = currentUnreadKey();
  const prev = queryClient.getQueryData<FormattedUnreadList>(key);
  const banners = prev?.barNotifyList?.notify_msgs;
  if (!banners?.length) return false;
  const next = banners.filter((b) => b.biz_id !== notificationId);
  if (next.length === banners.length) return false;
  queryClient.setQueryData<FormattedUnreadList>(key, {
    ...prev,
    barNotifyList: { ...prev!.barNotifyList, notify_msgs: next },
  });
  return true;
}

async function promptReLogin(): Promise<void> {
  // Shared latch with handleAuthError (axios 50001). Without it, a WS 50001
  // push concurrent with an axios 50001 response would stack two modals.
  if (!beginSignOut()) return;
  const t = i18n.global.t;
  // WS 50001 is intentionally confirm-driven: cancel leaves the user on the
  // page so a transient push doesn't force navigation. Redirect only when
  // the user confirms.
  try {
    await ElMessageBox.confirm(t('request.forcedLogoutContent'), t('request.sessionExpiredTitle'), {
      confirmButtonText: t('request.forcedLogoutConfirm'),
      cancelButtonText: t('common.cancel'),
      type: 'warning',
      customClass: 'sr-auth-dialog',
      showClose: false,
      closeOnClickModal: false,
      closeOnPressEscape: false,
    });
    useAuthStore().clearPersistedAuth();
    commitSignOut();
    location.assign(`${Path.Login}?step=choose`);
    // Lock stays latched: the old document lives until the hard-nav
    // commits, and releasing here would let a late 50001 stack a second
    // modal in that window.
  } catch {
    // User chose Cancel — stay on the page; any subsequent 50001 retriggers
    // via the axios interceptor's own flow.
    endSignOut();
  }
}

type WsMessage = {
  type?: string;
  code?: number;
  payload?: {
    type?: string;
    notificationId?: string;
    accountId?: string;
  };
};

// Report to monitoring instead of swallowing/logging. No payload is sent —
// a push frame can carry user data, and the type + stack are enough to triage.
function reportWsError(type: 'ws_message_parse_error' | 'ws_listener_error', ex: unknown): void {
  reportManualError(type, (ex instanceof Error ? ex.message : String(ex)) || 'websocket error', {
    stack: ex instanceof Error ? ex.stack : 'No stack trace',
  });
}

watch(wsData, (raw) => {
  if (!isString(raw) || !raw || raw === 'hb') return;
  let msg: unknown;
  try {
    msg = JSON.parse(raw);
  } catch (ex) {
    reportWsError('ws_message_parse_error', ex);
    return;
  }
  // Some push frames (notably GlobalEventTypePermissionChanged) are
  // double-encoded — backend applies JSON.stringify twice, so the first
  // parse yields a JSON string instead of the object. Re-parse in that case.
  if (typeof msg === 'string') {
    try {
      msg = JSON.parse(msg);
    } catch (ex) {
      reportWsError('ws_message_parse_error', ex);
      return;
    }
  }
  if (!msg || typeof msg !== 'object') return;
  const m = msg as WsMessage;
  try {
    if (m.code === FORCED_LOGOUT_CODE) {
      void promptReLogin();
    } else if (m.type === 'GlobalEventTypeNotificationReminder') {
      void refresh();
    } else if (m.type === 'GlobalEventTypeNotificationBroadcast') {
      if (m.payload?.type === 'publish') {
        void refresh();
      } else if (m.payload?.type === 'recall' && m.payload.notificationId) {
        if (removeBannerLocally(m.payload.notificationId)) void refresh();
      }
    } else if (m.type === 'GlobalEventTypePermissionChanged') {
      // Refresh perms; broadcaster's state-registry watch on user.perms /
      // user.dataPerms re-fires host:auth-changed, sub mirror updates, and
      // legacy router's permsWatcher (Task 3.8 follow-up) rebuilds matcher.
      void useUserStore().fetchPerms();
    } else if (m.type === 'GlobalEventTypeAccountStatusChanged' && m.payload?.accountId) {
      // Force a fresh fetch (not just invalidate) AND signal sub. The
      // state=-2 unsubscribe sign-out lives in the queryFn, so it must run
      // even on chrome-less routes where no component observes the query.
      refreshAccountInfo(queryClient, m.payload.accountId);
    }
  } catch (ex) {
    reportWsError('ws_listener_error', ex);
  }
  // Relay the parsed frame to sub, isolated from the handlers above. Host owns
  // the WS post-carving, but legacy views still register `ws.addMsgListener`
  // (cluster state, schedule policy, support permission). A throwing v3 handler
  // must not starve this relay — legacy guards each listener the same way.
  try {
    emitMessage(getSubBus(), HOST_EVENTS.WS_PUSH, m as Record<string, unknown>);
  } catch (ex) {
    reportWsError('ws_listener_error', ex);
  }
});
